import { useParams } from "react-router-dom";

import { LoadScreen, PageBackButton } from "@/core/components/shared";
import { Badge } from "@/core/components/ui/badge";
import { Button } from "@/core/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/core/components/ui/card";
import { NoTranslate } from "@/core/components/ui/no-translate";
import useTypedTranslation, {
  type TFunctionType,
} from "@/core/hooks/use-typed-translation";
import { formatDateWithFallback } from "@/core/utils/func/formatDateWithFallback";
import { useWallet } from "@/features/wallet/api/use-wallet";
import { usePartyNames } from "@/features/kyc/api/use-party-names";
import {
  BeneficiariesSection,
  WalletBalanceCard,
  WalletStatementRequestCard,
  WalletStatusActions,
} from "@/features/wallet/components";
import type { WalletStatus } from "@/features/shared/api/enums";

const STATUS_VARIANTS: Record<
  WalletStatus,
  "warning" | "success" | "destructive" | "secondary"
> = {
  PENDING_KYC: "warning",
  ACTIVE: "success",
  SUSPENDED: "destructive",
  CLOSED: "secondary",
};

/**
 * Détail d'un portefeuille (`GET /Wallets/{id}`) — écran solde + infos +
 * actions d'orchestration (statut, kyc-approval étape 6) + bénéficiaires
 * (ressource 3.3). Données financières et références en `NoTranslate`.
 */
export function WalletDetailPage() {
  const { t } = useTypedTranslation();
  // Les clés wallet.wallets.* / wallet.beneficiaries.* vivent dans
  // features/wallet/i18n/ en attendant leur fusion dans src/locales.
  const tWallet = (key: string) => t(key as TFunctionType);
  const { walletId = "" } = useParams();
  const { data: wallet, isLoading, error, refetch } = useWallet(walletId);
  // Phase 14 — Delta v2.3 : résolution du nom d'affichage du titulaire via
  // le lookup collectif `POST /Parties/names` (lot d'une seule entrée ici,
  // mais le hook mutualise le cache pour les autres pages).
  const { nameByPartyId } = usePartyNames(
    wallet?.ownerPartyId ? [wallet.ownerPartyId] : []
  );
  const ownerDisplayName = wallet?.ownerPartyId
    ? nameByPartyId.get(wallet.ownerPartyId)
    : undefined;
  const backButton = (
    <PageBackButton to="/app/wallet" label={t("common.back")} />
  );

  if (isLoading) {
    return (
      <section className="flex flex-col gap-4">
        {backButton}
        <div className="flex h-64 items-center justify-center">
          <LoadScreen />
        </div>
      </section>
    );
  }

  if (error || !wallet) {
    return (
      <section className="flex flex-col gap-4">
        {backButton}
        <div className="flex flex-col items-center gap-4 py-24">
          <p className="font-semibold text-destructive">
            {error?.message ?? t("error")}
          </p>
          <Button variant="secondary" size="sm" onClick={() => void refetch()}>
            {t("reload")}
          </Button>
        </div>
      </section>
    );
  }

  const fields: { label: string; value?: string }[] = [
    { label: tWallet("wallet.wallets.detail.id"), value: wallet.id },
    {
      label: tWallet("wallet.wallets.columns.ownerPartyId"),
      value: ownerDisplayName
        ? `${ownerDisplayName} — ${wallet.ownerPartyId}`
        : wallet.ownerPartyId,
    },
    {
      label: tWallet("wallet.wallets.form.keycloakUserId"),
      value: wallet.keycloakUserId,
    },
    {
      label: tWallet("wallet.wallets.columns.currency"),
      value: wallet.currency,
    },
    {
      label: tWallet("wallet.wallets.detail.version"),
      value: wallet.version != null ? String(wallet.version) : undefined,
    },
    {
      label: tWallet("wallet.wallets.detail.createdAt"),
      value: wallet.createdAt
        ? formatDateWithFallback(wallet.createdAt, "—")
        : undefined,
    },
    {
      label: tWallet("wallet.wallets.detail.updatedAt"),
      value: wallet.updatedAt
        ? formatDateWithFallback(wallet.updatedAt, "—")
        : undefined,
    },
  ];

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          {backButton}
          <h1 className="text-2xl font-semibold text-foreground">
            <NoTranslate as="span">
              {wallet.walletNumber || wallet.id}
            </NoTranslate>
          </h1>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <NoTranslate as="span">
              <Badge variant="secondary">{wallet.ownerType}</Badge>
            </NoTranslate>
            {wallet.kycLevel ? (
              <NoTranslate as="span">
                <Badge variant="outline">{wallet.kycLevel}</Badge>
              </NoTranslate>
            ) : null}
            {wallet.status ? (
              <NoTranslate as="span">
                <Badge
                  variant={STATUS_VARIANTS[wallet.status] ?? "outline"}
                >
                  {wallet.status}
                </Badge>
              </NoTranslate>
            ) : null}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {fields.map(({ label, value }) => (
              <div key={label}>
                <dt className="text-sm text-muted-foreground">{label}</dt>
                <dd className="font-medium">
                  <NoTranslate as="span" className="break-all">
                    {value || "—"}
                  </NoTranslate>
                </dd>
              </div>
            ))}
          </dl>
        </CardContent>
      </Card>

      <WalletBalanceCard walletId={walletId} currency={wallet.currency} />

      <WalletStatusActions wallet={wallet} />

      <WalletStatementRequestCard walletId={walletId} />

      <BeneficiariesSection walletId={walletId} />
    </section>
  );
}
